import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Map, OrderedMap, Set } from 'immutable';
import { FormattedMessage } from 'react-intl';
import VisibilitySensor from 'react-visibility-sensor';

import User from 'models/user';

import { formatPhoneNumber } from 'lib/common/format-phone-number';
import {
  INPUT_IMPRESSION,
  INPUT_CLICK,
  INPUT_FOCUS,
  MONTHLY_SEARCH_MODAL_CONTACT_FORM_IMPRESSION,
  MONTHLY_SEARCH_MODAL_CONTACT_FORM_SUBMIT,
  SUBMIT_BUTTON_CLICK,
  SUBMIT_BUTTON_IMPRESSION,
} from 'lib/analytics/events';

import EmailSuggestion from 'components/search/monthly-form/email-suggestion';
import RequiredField from 'components/common/required-field';

const INPUT_CHANGE_TIMEOUT = 500;
export const CONTACT_STEP = 'Contact';

const propTypes = {
  user: PropTypes.instanceOf(User).isRequired,
  contactPhone: PropTypes.string.isRequired,
  step: PropTypes.string.isRequired,
  requiredFields: PropTypes.instanceOf(Set),
  requiredFieldErrors: PropTypes.oneOfType([
    PropTypes.instanceOf(Map),
    PropTypes.instanceOf(OrderedMap),
  ]),
  onSubmit: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
  modifyRequiredFieldList: PropTypes.func,
};

const defaultProps = {
  requiredFields: Set(),
  requiredFieldErrors: Map(),
  modifyRequiredFieldList: () => {},
};

class MonthlyFormContact extends Component {
  constructor(props) {
    super(props);

    const { name, email, phone } = props.user;
    this.state = { name, email, phone };

    this.onSubmit = this.onSubmit.bind(this);
    this.onSubmitClick = this.onSubmitClick.bind(this);
    this.onNameChange = this.onNameChange.bind(this);
    this.onEmailChange = this.onEmailChange.bind(this);
    this.onPhoneChange = this.onPhoneChange.bind(this);
    this.onInputClick = this.onInputClick.bind(this);
    this.onInputFocus = this.onInputFocus.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.useSuggestion = this.useSuggestion.bind(this);
  }

  onSubmit(e) {
    e.preventDefault();
    this.props.trackEvent({
      ...MONTHLY_SEARCH_MODAL_CONTACT_FORM_SUBMIT,
      properties: {
        Name: this.state.name,
        Phone: this.state.phone,
        Email: this.state.email,
      },
    });
    this.props.onSubmit(this.state);
  }

  onSubmitClick(e) {
    this.props.trackEvent(SUBMIT_BUTTON_CLICK);
    this.onSubmit(e);
  }

  onNameChange() {
    this.setState({ name: this.name.value });
  }

  onEmailChange() {
    this.setState({ email: this.email.value });
  }

  onPhoneChange() {
    clearTimeout(this.phoneNumberChangeTimeout);
    const target = this.phone;
    let newNumber = target.value.replace(/\D/g, '');
    const prevNumber = this.state.phone;
    const lastChar = prevNumber ? formatPhoneNumber(prevNumber).slice(-1)[0] : '';
    const maxLength = newNumber[0] === '1' ? 11 : 10;

    // To handle case of deleting pretty chars ( '(', ')', '-' ) etc
    if (prevNumber === newNumber && lastChar.match(/[()\-+ ]/)) { newNumber = newNumber.slice(0, -1); }
    // Don't let user enter more numbaZ
    newNumber = newNumber.substring(0, maxLength);
    this.setState({ phone: newNumber });
    this.phoneNumberChangeTimeout = setTimeout(this.onPhoneNumberChange, INPUT_CHANGE_TIMEOUT);
  }

  onVisibilityChange(v) {
    if (v) {
      const { trackEvent } = this.props;
      trackEvent(MONTHLY_SEARCH_MODAL_CONTACT_FORM_IMPRESSION);
      trackEvent(SUBMIT_BUTTON_IMPRESSION);
      trackEvent({ ...INPUT_IMPRESSION, name: 'Name' });
      trackEvent({ ...INPUT_IMPRESSION, name: 'Email' });
      trackEvent({ ...INPUT_IMPRESSION, name: 'Phone' });
    }
  }

  onInputClick(name) {
    this.props.trackEvent({ ...INPUT_CLICK, name });
  }

  onInputFocus(name) {
    this.props.trackEvent({ ...INPUT_FOCUS, name });
  }

  useSuggestion(suggestion) {
    this.email.value = suggestion;
    this.setState({ email: suggestion });
  }

  render() {
    const { contactPhone, step } = this.props;
    const { phone } = this.state;

    if (step !== CONTACT_STEP) { return null; }

    return (
      <VisibilitySensor onChange={this.onVisibilityChange}>
        <Fragment>
          <div className="row margin-bottom-10 text-color-dark-slate">
            <div className="col-xs-12 text-size-16 text-weight-bold">
              <FormattedMessage
                id="search.send-info"
                defaultMessage="SEND US YOUR INFO"
              />
            </div>
          </div>
          <div className="row margin-bottom-10 text-color-dark-slate">
            <div className="col-xs-12 text-size-12 text-weight-book">
              <FormattedMessage
                id="search.contact-let-us-know"
                defaultMessage="Let us know the best way to reach you and we'll be in touch shortly"
              />
            </div>
          </div>
          <div className="row margin-bottom-10 text-color-dark-slate">
            <div className="col-xs-12 text-size-12 text-weight-bold">
              <FormattedMessage
                id="search.contact-phone"
                defaultMessage="Want to get started now? Just call us at {phone}"
                values={{
                  phone: <a href={`tel:${contactPhone}`}>{contactPhone}</a>,
                }}
              />
            </div>
          </div>
          <form id="monthly-form" onSubmit={this.onSubmit}>
            <div className="row">
              <div className="col-xs-12">
                <div className="form-group">
                  <label className="required-field text-size-10" htmlFor="name">NAME</label>
                  <RequiredField
                    key="name"
                    modifyRequiredFieldList={this.props.modifyRequiredFieldList}
                    requiredFieldErrors={this.props.requiredFieldErrors}
                    requiredFields={this.props.requiredFields}
                  >
                    <input
                      className="form-control short"
                      type="text"
                      id="name"
                      name="name"
                      data-field-name="name"
                      required
                      placeholder="Full Name"
                      defaultValue={this.state.name}
                      ref={(nameInput) => { this.name = nameInput; }}
                      onChange={this.onNameChange}
                      onClick={() => { this.onInputClick('Name'); }}
                      onFocus={() => { this.onInputFocus('Name'); }}
                    />
                  </RequiredField>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-xs-7">
                <div className="form-group">
                  <label className="required-field text-size-10" htmlFor="email">EMAIL</label>
                  <RequiredField
                    key="email"
                    modifyRequiredFieldList={this.props.modifyRequiredFieldList}
                    requiredFieldErrors={this.props.requiredFieldErrors}
                    requiredFields={this.props.requiredFields}
                  >
                    <input
                      className="form-control short"
                      type="email"
                      id="email"
                      name="email"
                      data-field-name="email"
                      required
                      placeholder="name@email.com"
                      defaultValue={this.state.email}
                      ref={(emailInput) => { this.email = emailInput; }}
                      onChange={this.onEmailChange}
                      onClick={() => { this.onInputClick('Email'); }}
                      onFocus={() => { this.onInputFocus('Email'); }}
                    />
                  </RequiredField>
                  <EmailSuggestion email={this.state.email} useSuggestion={this.useSuggestion} />
                </div>
              </div>
              <div className="col-xs-5">
                <div className="form-group">
                  <label className="required-field text-size-10" htmlFor="phone">PHONE NUMBER</label>
                  <RequiredField
                    key="phone"
                    modifyRequiredFieldList={this.props.modifyRequiredFieldList}
                    requiredFieldErrors={this.props.requiredFieldErrors}
                    requiredFields={this.props.requiredFields}
                  >
                    <input
                      className="form-control short"
                      type="tel"
                      id="phone"
                      name="phone"
                      data-field-name="phone_number"
                      required
                      placeholder="(___)___-____"
                      value={formatPhoneNumber(phone)}
                      ref={(phoneNumber) => { this.phone = phoneNumber; }}
                      onChange={this.onPhoneChange}
                      onClick={() => { this.onInputClick('Phone'); }}
                      onFocus={() => { this.onInputFocus('Phone'); }}
                    />
                  </RequiredField>
                </div>
              </div>
            </div>
          </form>
          <div className="row margin-bottom-30">
            <div className="col-xs-4 col-xs-offset-4" >
              <button type="submit" className="btn btn-primary btn-blue" onClick={this.onSubmitClick} onSubmit={this.onSubmit}>
                <FormattedMessage
                  id="common.submit"
                  defaultMessage="SUBMIT"
                  className="text-size-14"
                />
              </button>
            </div>
          </div>
        </Fragment>
      </VisibilitySensor>
    );
  }
}

MonthlyFormContact.propTypes = propTypes;
MonthlyFormContact.defaultProps = defaultProps;
export default MonthlyFormContact;
